import { ChangeDetectionStrategy, Component, OnInit } from '@angular/core';
import { catchError, EMPTY, map, Observable, of, take, tap } from 'rxjs';
import { ActivatedRoute, Router } from '@angular/router';

import { PortalLeadService } from '@app/services/portal-lead/portal-lead.service';
import {
  GoogleFlightsFeedbackOptionEnum,
  PortalFeatures,
  PortalLeadNode,
  PortalOrderNode,
  PortalOrderQuoteCardNode,
} from '@app/services/api/api.types';
import { PopupsService } from '@voyagu/portal-design-system';
import { PortalOrderService } from '@app/services/portal-order/portal-order.service';
import { AuthService, User } from '@app/services/auth/auth.service';
import { State, StateService } from '@app/services/state/state.service';
import { PortalTrackingEvents } from '@app/utils/tracking-events';
import { Tracking } from '@app/services/tracking/tracking.service';
import { getOptionSoldAmount } from '@app/utils/utils';
import { ApiService } from '@app/services/api/api.service';
import { GoogleFlightOption } from '@app/services/google-flights/google-flights.types';
import { GoogleFlightsService } from '@app/services/google-flights/google-flights.service';
import { ErrorService } from '@app/services/error.service';

@Component({
  selector: 'lead-page',
  templateUrl: './lead-page.component.html',
  styleUrls: ['./lead-page.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class LeadPageComponent implements OnInit {
  GoogleFlightsFeedbackOptionEnum = GoogleFlightsFeedbackOptionEnum;
  getOptionSoldAmount = getOptionSoldAmount;

  PortalTrackingEvents = PortalTrackingEvents;

  portalLead$: Observable<PortalLeadNode | null>;

  leastExpensiveSoldAmount: number | undefined;
  portalOrder: PortalOrderNode;
  state: State;

  // portalLink of the option whose full ``portalOrder`` we are fetching before
  // navigation; used to disable the option's request-to-book button so a
  // double-click cannot fire two concurrent fetches (TIC-6859 payload split).
  submittingPortalLink: string | null = null;

  withHiddenFlightsV2: boolean;

  user: User | null;

  flightOption$: Observable<GoogleFlightOption | null>;

  loadedFlightOption: boolean;

  showAgentDetails: boolean;

  selectedFeedbackOption: GoogleFlightsFeedbackOptionEnum;

  constructor(
    private googleFlightsService: GoogleFlightsService,
    private portalLeadService: PortalLeadService,
    private portalOrderService: PortalOrderService,
    private activatedRoute: ActivatedRoute,
    private stateService: StateService,
    private popupsService: PopupsService,
    private authService: AuthService,
    private apiService: ApiService,
    private errorService: ErrorService,
    private router: Router,
  ) {}

  ngOnInit() {
    this.getPortalLead();

    this.user = this.authService.getUser();
  }

  getPortalLead() {
    this.portalOrderService.setPortalOrder(null);

    this.portalLead$ = this.portalLeadService.portalLead$.pipe(
      map((portalLead) => {
        if (portalLead) {
          this.withHiddenFlightsV2 = portalLead.salesAgent?.portalFeatures?.includes(
            PortalFeatures.portal_hidden_flights_v2,
          );
          const options = portalLead.flightRequest?.publishedOptions?.edges;
          this.leastExpensiveSoldAmount = this.findMinSoldAmount(options);

          this.flightOption$ = this.googleFlightsService.getFlight(portalLead).pipe(
            tap(() => {
              this.loadedFlightOption = true;
            }),
            catchError(() => {
              this.loadedFlightOption = true;
              return of(null);
            }),
          );
        }

        this.state = this.stateService.getState();

        return portalLead;
      }),
    );
  }

  requestToBook(option: PortalOrderQuoteCardNode) {
    if (this.submittingPortalLink) {
      return;
    }
    this.hideModal();

    // Fetch the full ``portalOrder`` for the picked option before navigating —
    // ``portalLead.publishedOptions`` returns only the slim card shape, so we
    // must hydrate ``PortalOrderService`` with the full checkout-shape node
    // that downstream pages (option, overview-and-payment, ancillary flows,
    // tiers) read from without their own refetch (TIC-6859 payload split).
    this.submittingPortalLink = option.portalLink;
    this.apiService
      .portalOrder({ portalLink: option.portalLink })
      .pipe(
        take(1),
        catchError((error: unknown) => {
          console.log(error instanceof Error ? error.message : error);
          this.submittingPortalLink = null;
          this.errorService.showCommonError();
          return EMPTY;
        }),
      )
      .subscribe((response) => {
        this.submittingPortalLink = null;
        if (!response?.portalOrder) {
          this.errorService.showCommonError();
          return;
        }
        this.stateService.resetState();
        this.portalOrderService.setPortalOrder(response.portalOrder);
        this.portalOrder = response.portalOrder;
        Tracking.track(PortalTrackingEvents.portalOptionSelected);
        this.submitRequestToBook();
      });
  }

  submitRequestToBook() {
    const portalLink = this.stateService.getState().portalLink;
    const queryParams = this.activatedRoute.snapshot.queryParams;

    void this.router.navigate([`${portalLink}`, `${this.portalOrder.portalLink}`], {
      queryParams,
    });
  }

  hideModal() {
    this.popupsService.hideLast();
  }

  goBack() {
    void this.router.navigate([``], { queryParamsHandling: 'merge' });
  }

  findMinSoldAmount(options: { node: PortalOrderQuoteCardNode }[]): number | undefined {
    if (!options?.length) {
      return;
    }

    return options.reduce(
      (min, option) => Math.min(min, this.getOptionSoldAmount(option.node)),
      +Infinity,
    );
  }

  submitQuickquote(input: {
    leadPortalLink: string;
    feedbackOption: GoogleFlightsFeedbackOptionEnum;
    budgetFrom: number;
    budgetTo: number;
    comment: string;
    routeDetails: {
      origin: string;
      destination: string;
      date: string;
      airline: string;
    };
    googlePrice: string;
    displayPrice: string;
    markdownPercentage: number;
  }) {
    this.apiService.saveGoogleFlightsBaselineFeedback({ input }).subscribe((data) => {
      if (data?.ok) {
        this.selectedFeedbackOption = input.feedbackOption;
        this.portalLeadService.setPortalLead(data.portalLead);
      }
    });
  }
}
